import { create } from "zustand";

/**
 * Identity search result from the API.
 */
export interface IdentitySearchResult {
  id: string;
  displayName: string;
  canonicalEmail: string;
  avatarUrl?: string;
  availableServices: string[];
  matchScore?: number;
  /** Service-specific user IDs (e.g., Jira accountId, GitHub user ID) for direct API queries */
  serviceUserIds?: Record<string, string>;
}

/**
 * Pending link awaiting confirmation.
 */
export interface PendingLinkInfo {
  identityId: string;
  pendingId: string;
  serviceName: string;
  displayName: string;
  matchScore: number;
}

/**
 * Mention Store State
 *
 * Manages:
 * - Search result caching for performance
 * - Recently used identities for quick access
 * - Pending links awaiting user confirmation
 */
export interface MentionStoreState {
  // Search result cache (key: query, value: results)
  cache: Record<string, IdentitySearchResult[]>;
  cacheTimestamps: Record<string, number>;

  // Recently used identities (for quick access)
  recentIdentities: IdentitySearchResult[];

  // Pending links awaiting confirmation
  pendingLinks: PendingLinkInfo[];

  // Actions
  setCache: (key: string, results: IdentitySearchResult[]) => void;
  getCache: (key: string) => IdentitySearchResult[] | null;
  clearCache: () => void;

  addRecentIdentity: (identity: IdentitySearchResult) => void;
  clearRecentIdentities: () => void;

  setPendingLinks: (links: PendingLinkInfo[]) => void;
  removePendingLink: (pendingId: string) => void;
}

/** Maximum number of cached queries */
const MAX_CACHE_SIZE = 50;

/** Maximum number of recent identities to store */
const MAX_RECENT = 10;

/** Cache entry TTL in milliseconds (5 minutes) */
const CACHE_TTL_MS = 5 * 60 * 1000;

export const useMentionStore = create<MentionStoreState>((set, get) => ({
  cache: {},
  cacheTimestamps: {},
  recentIdentities: [],
  pendingLinks: [],

  /**
   * Cache search results with TTL.
   */
  setCache: (key: string, results: IdentitySearchResult[]) => {
    const normalizedKey = key.toLowerCase().trim();

    set((state) => {
      const newCache = { ...state.cache };
      const newTimestamps = { ...state.cacheTimestamps };

      // Enforce cache size limit (LRU eviction)
      const keys = Object.keys(newCache);

      if (keys.length >= MAX_CACHE_SIZE) {
        // Find oldest entry
        let oldestKey = keys[0];
        let oldestTime = newTimestamps[oldestKey] || 0;

        for (const k of keys) {
          const time = newTimestamps[k] || 0;

          if (time < oldestTime) {
            oldestKey = k;
            oldestTime = time;
          }
        }

        delete newCache[oldestKey];
        delete newTimestamps[oldestKey];
      }

      newCache[normalizedKey] = results;
      newTimestamps[normalizedKey] = Date.now();

      return { cache: newCache, cacheTimestamps: newTimestamps };
    });
  },

  /**
   * Get cached results if not expired.
   */
  getCache: (key: string) => {
    const normalizedKey = key.toLowerCase().trim();
    const state = get();
    const timestamp = state.cacheTimestamps[normalizedKey];

    if (!timestamp) {
      return null;
    }

    // Check if expired
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      // Expired - remove from cache
      set((s) => {
        const newCache = { ...s.cache };
        const newTimestamps = { ...s.cacheTimestamps };

        delete newCache[normalizedKey];
        delete newTimestamps[normalizedKey];

        return { cache: newCache, cacheTimestamps: newTimestamps };
      });

      return null;
    }

    return state.cache[normalizedKey] || null;
  },

  /**
   * Clear all cached results.
   */
  clearCache: () => {
    set({ cache: {}, cacheTimestamps: {} });
  },

  /**
   * Add an identity to recent list (most recent first).
   * Removes duplicates and enforces max size.
   */
  addRecentIdentity: (identity: IdentitySearchResult) => {
    set((state) => {
      // Remove if already exists
      const filtered = state.recentIdentities.filter((i) => i.id !== identity.id);

      // Add to front, limit size
      return {
        recentIdentities: [identity, ...filtered].slice(0, MAX_RECENT),
      };
    });
  },

  /**
   * Clear all recent identities.
   */
  clearRecentIdentities: () => {
    set({ recentIdentities: [] });
  },

  /**
   * Set pending links from API.
   */
  setPendingLinks: (links: PendingLinkInfo[]) => {
    set({ pendingLinks: links });
  },

  /**
   * Remove a pending link after confirmation/rejection.
   */
  removePendingLink: (pendingId: string) => {
    set((state) => ({
      pendingLinks: state.pendingLinks.filter((pl) => pl.pendingId !== pendingId),
    }));
  },
}));
